import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { AlertTriangle, X, Check, Mail } from 'lucide-react';
import { cn, getInitials, getAvatarColor, Popover, PopoverContent, PopoverTrigger } from '@clarix/ui';
import type { TeamMemberApiItem } from '@clarix/shared-types';
import type { User } from '@clarix/shared-types';
import type { PeoplePickerPerson } from './meeting-form.types';

interface UnifiedPeoplePickerProps {
  selected: PeoplePickerPerson[];
  onSelectedChange: (people: PeoplePickerPerson[]) => void;
  /** Project team members (undefined for org-level meetings) */
  teamMembers?: TeamMemberApiItem[];
  /** All organization users */
  allUsers: User[];
  disabled?: boolean;
  /** Trigger classes — lets the caller give it the surface's field look. */
  className?: string;
  /** People busy in the draft slot — chips and rows get a conflict mark. */
  conflictPersonIds?: ReadonlySet<string>;
  /** Tooltip / row hint for a conflicting person (e.g. overlapping titles). */
  conflictHint?: (personId: string) => string | undefined;
}

export function UnifiedPeoplePicker({
  selected,
  onSelectedChange,
  teamMembers,
  allUsers,
  disabled = false,
  className,
  conflictPersonIds,
  conflictHint,
}: UnifiedPeoplePickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Focus input when popover opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const teamMemberIds = useMemo(
    () => new Set(teamMembers?.map((tm) => tm.user_id) ?? []),
    [teamMembers],
  );

  // Build org users (non-team) list
  const orgUsers = useMemo(
    () =>
      teamMembers
        ? allUsers.filter((u) => !teamMemberIds.has(u.id))
        : allUsers,
    [allUsers, teamMembers, teamMemberIds],
  );

  // Filter by search
  const searchLower = search.toLowerCase();

  const filteredTeam = useMemo(
    () =>
      (teamMembers ?? []).filter(
        (m) =>
          m.user_name.toLowerCase().includes(searchLower) ||
          m.user_email?.toLowerCase().includes(searchLower),
      ),
    [teamMembers, searchLower],
  );

  const filteredOrg = useMemo(
    () =>
      orgUsers.filter(
        (u) =>
          u.name.toLowerCase().includes(searchLower) ||
          u.email?.toLowerCase().includes(searchLower),
      ),
    [orgUsers, searchLower],
  );

  const selectedIds = useMemo(
    () => new Set(selected.map((p) => p.id)),
    [selected],
  );

  const handleToggle = useCallback(
    (person: PeoplePickerPerson) => {
      if (selectedIds.has(person.id)) {
        onSelectedChange(selected.filter((p) => p.id !== person.id));
      } else {
        onSelectedChange([...selected, person]);
      }
      setSearch('');
    },
    [selected, selectedIds, onSelectedChange],
  );

  const handleRemove = useCallback(
    (id: string) => {
      onSelectedChange(selected.filter((p) => p.id !== id));
    },
    [selected, onSelectedChange],
  );

  const handleAddExternal = useCallback(() => {
    const trimmed = emailInput.trim();
    if (!trimmed || !trimmed.includes('@')) return;
    // Avoid duplicates
    if (selected.some((p) => p.email === trimmed)) return;
    const name = trimmed.split('@')[0] ?? trimmed;
    onSelectedChange([
      ...selected,
      {
        id: `ext-${Date.now()}`,
        name,
        email: trimmed,
        type: 'external',
      },
    ]);
    setEmailInput('');
  }, [emailInput, selected, onSelectedChange]);

  const showEmailHint =
    search.includes('@') && search.trim().length > 3;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          onClick={() => !disabled && setOpen(true)}
          className={cn(
            // Field grammar: same 28px height and text size as every other field
            // on the surface — the caller passes the look (boxed chip or blank
            // canvas) through `className` (see detail-chip-styles).
            'flex min-h-[28px] max-h-[70px] flex-wrap items-center gap-1.5 overflow-y-auto overflow-x-hidden rounded-[9px] border border-gray-200 bg-white px-2.5 py-0.5 text-lg transition-colors hover:border-gray-300',
            open && 'border-indigo-400',
            disabled
              ? 'cursor-not-allowed opacity-50'
              : 'cursor-text',
            className,
          )}
        >
          {/* No leading glyph: this picker only ever sits in a property row,
              whose label column already carries the attendees icon. */}
          {selected.map((person) => (
            <PersonChip
              key={person.id}
              person={person}
              onRemove={() => handleRemove(person.id)}
              conflict={conflictPersonIds?.has(person.id) === true}
              conflictHint={conflictHint?.(person.id)}
            />
          ))}
          <input
            ref={inputRef}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              if (!open && e.target.value) setOpen(true);
            }}
            placeholder={
              selected.length === 0 ? 'Add people...' : ''
            }
            className="min-w-[80px] flex-1 border-0 bg-transparent p-0 text-lg text-foreground placeholder:text-muted-foreground/60 focus:outline-none"
            disabled={disabled}
          />
        </div>
      </PopoverTrigger>

      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div
          ref={listRef}
          className="max-h-[320px] overflow-y-auto overflow-x-hidden"
          onWheel={(e) => e.stopPropagation()}
        >
          {/* Project Team section */}
          {teamMembers && filteredTeam.length > 0 && (
            <div className="px-3 pb-1 pt-2">
              <div className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                Project Team
              </div>
              {filteredTeam.map((member) => {
                const isSelected = selectedIds.has(member.user_id);
                const busy = conflictPersonIds?.has(member.user_id) === true;
                return (
                  <PersonRow
                    key={member.user_id}
                    name={member.user_name}
                    subtitle={member.designation_title}
                    isSelected={isSelected}
                    conflict={busy}
                    conflictHint={busy ? conflictHint?.(member.user_id) : undefined}
                    onClick={() =>
                      handleToggle({
                        id: member.user_id,
                        name: member.user_name,
                        email: member.user_email,
                        role: member.designation_title ?? undefined,
                        type: 'team',
                      })
                    }
                  />
                );
              })}
            </div>
          )}

          {/* Organization section */}
          {filteredOrg.length > 0 && (
            <div className="px-3 pb-1 pt-2">
              <div className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                {teamMembers ? 'Organization' : 'Users'}
              </div>
              {filteredOrg.map((user) => {
                const isSelected = selectedIds.has(user.id);
                const busy = conflictPersonIds?.has(user.id) === true;
                return (
                  <PersonRow
                    key={user.id}
                    name={user.name}
                    subtitle={user.designation_title ?? user.template_name}
                    isSelected={isSelected}
                    conflict={busy}
                    conflictHint={busy ? conflictHint?.(user.id) : undefined}
                    onClick={() =>
                      handleToggle({
                        id: user.id,
                        name: user.name,
                        email: user.email,
                        role: user.designation_title ?? user.template_name ?? undefined,
                        type: 'org',
                      })
                    }
                  />
                );
              })}
            </div>
          )}

          {/* Empty state */}
          {filteredTeam.length === 0 &&
            filteredOrg.length === 0 &&
            !showEmailHint && (
              <div className="px-3 py-4 text-center text-lg text-muted-foreground">
                No users found
              </div>
            )}

          {/* External guest section */}
          <div className="border-t border-border px-3 py-2">
            <div className="mb-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
              External guest
            </div>
            {showEmailHint && (
              <button
                type="button"
                onClick={() => {
                  setEmailInput(search);
                  setSearch('');
                  // Defer to let state settle, then add
                  setTimeout(() => {
                    const email = search.trim();
                    if (email.includes('@')) {
                      onSelectedChange([
                        ...selected,
                        {
                          id: `ext-${Date.now()}`,
                          name: email.split('@')[0] ?? email,
                          email,
                          type: 'external',
                        },
                      ]);
                      setSearch('');
                    }
                  }, 0);
                }}
                className="mb-2 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xl text-indigo-600 hover:bg-indigo-50"
              >
                <Mail className="h-3.5 w-3.5" strokeWidth={1.5} />
                Invite {search.trim()}
              </button>
            )}
            <div className="flex gap-1.5">
              <input
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddExternal();
                  }
                }}
                placeholder="name@client.com"
                className="flex-1 rounded-md border border-border bg-muted/30 px-2 py-1.5 text-lg text-foreground placeholder:text-muted-foreground focus:border-indigo-400 focus:outline-none"
              />
              <button
                type="button"
                onClick={handleAddExternal}
                disabled={
                  !emailInput.trim() ||
                  !emailInput.includes('@')
                }
                className={cn(
                  'rounded-md px-2.5 py-1.5 text-lg font-semibold transition-colors',
                  emailInput.includes('@')
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ── Sub-components ──────────────────────────────────────────────────

interface PersonChipProps {
  person: PeoplePickerPerson;
  onRemove: () => void;
  conflict?: boolean;
  conflictHint?: string;
}

function PersonChip({ person, onRemove, conflict = false, conflictHint }: PersonChipProps) {
  const initials = getInitials(person.name);
  const bgColor = getAvatarColor(initials);
  const firstName = person.name.split(' ')[0] ?? person.name;
  // Native title — chips sit inside the popover trigger; a Tooltip portal nests badly here.
  const tip = conflict ? (conflictHint ? `Busy · ${conflictHint}` : 'Busy') : undefined;

  return (
    <span
      title={tip}
      className={cn(
        'inline-flex items-center gap-1 rounded-full py-0.5 pl-0.5 pr-2 text-lg font-medium',
        conflict
          ? 'bg-warning-light text-warning-fg ring-1 ring-inset ring-warning/40'
          : 'bg-muted text-muted-foreground',
      )}
    >
      <span
        className={cn(
          'relative flex h-[18px] w-[18px] items-center justify-center rounded-full text-xxs font-semibold text-white',
          bgColor,
        )}
      >
        {initials}
        {conflict && (
          <span className="absolute -bottom-0.5 -right-0.5 flex h-2.5 w-2.5 items-center justify-center rounded-full bg-warning text-white ring-1 ring-background">
            <AlertTriangle className="h-1.5 w-1.5" strokeWidth={3} aria-hidden />
          </span>
        )}
      </span>
      <span className="pl-0.5">{firstName}</span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        aria-label={`Remove ${person.name}`}
        className={cn(
          'ml-0.5 hover:text-destructive',
          conflict ? 'text-warning-fg' : 'text-muted-foreground',
        )}
      >
        <X className="h-3 w-3" strokeWidth={1.5} />
      </button>
    </span>
  );
}

interface PersonRowProps {
  name: string;
  subtitle: string | null;
  isSelected: boolean;
  onClick: () => void;
  conflict?: boolean;
  conflictHint?: string;
}

function PersonRow({
  name,
  subtitle,
  isSelected,
  onClick,
  conflict = false,
  conflictHint,
}: PersonRowProps) {
  const initials = getInitials(name);
  const bgColor = getAvatarColor(initials);
  // Yellow row already marks conflict; keep the line short — full title is on the chip hover.
  const busyLine = conflict ? 'Busy' : null;

  return (
    <button
      type="button"
      title={conflict && conflictHint ? `Busy · ${conflictHint}` : undefined}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors',
        conflict
          ? 'bg-warning-light text-warning-fg ring-1 ring-inset ring-warning/30 hover:bg-warning/20'
          : isSelected
            ? 'bg-indigo-50'
            : 'hover:bg-muted',
      )}
    >
      <span
        className={cn(
          'relative flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white',
          bgColor,
        )}
      >
        {initials}
        {conflict && (
          <span className="absolute -bottom-0.5 -right-0.5 flex h-2.5 w-2.5 items-center justify-center rounded-full bg-warning text-white ring-1 ring-background">
            <AlertTriangle className="h-1.5 w-1.5" strokeWidth={3} aria-hidden />
          </span>
        )}
      </span>
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            'truncate text-xl font-medium',
            conflict ? 'text-warning-fg' : 'text-foreground',
          )}
        >
          {name}
        </div>
        {busyLine ? (
          <div className="truncate text-sm font-medium text-warning-fg">{busyLine}</div>
        ) : (
          subtitle && (
            <div className="truncate text-sm text-muted-foreground">
              {subtitle}
            </div>
          )
        )}
      </div>
      {isSelected && (
        <Check
          className={cn(
            'h-3.5 w-3.5 shrink-0',
            conflict ? 'text-warning-fg' : 'text-indigo-600',
          )}
          strokeWidth={2.5}
        />
      )}
    </button>
  );
}
